<template>
  <h1>Aprobador de solicitudes de cartas</h1>
  <Toolbar class="w-11 mt-3">
    <template #start>
      <Button
        @click="changeRequestHistoric"
        class="font-medium cursor-pointer"
        severity="secondary"
      >
        <i v-if="!requestsAreHistoric" class="pi pi-history mr-2" />
        <i v-else class="pi pi-fw pi-eye mr-2" />
        <label class="cursor-pointer" v-if="!requestsAreHistoric"
          >Ver historial</label
        >
        <label class="cursor-pointer" v-else="requestsAreHistoric"
          >Ver pendientes</label
        >
      </Button>
    </template>
    <template #end>
      <Button
        label="Procesar selección"
        icon="pi pi-check"
        severity="primary"
        @click="processMultipleRequests"
        :disabled="
          !(selectedRequests && selectedRequests.length > 0) ||
          requestsAreHistoric
        "
        class="font-light text-sm"
      />
    </template>
  </Toolbar>
  <DataTable
    v-model:selection="selectedRequests"
    :value="letterRequests"
    dataKey="id"
    v-model:filters="filters"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    filter-display="row"
    tableStyle="min-width: 60rem"
    currentPageReportTemplate="{first} a {last} de {totalRecords}"
    class="tabla mb-3"
    paginator
    :rows="10"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    removable-sort
    sort-field="updatedAt"
    :sort-order="-1"
    :globalFilterFields="[
      'employee.name',
      'employee.code',
      'employee.payroll.code',
      'employee.payroll.name',
      'letterCertifConfig.name',
      'createdAt',
    ]"
  >
    <template #header>
      <div class="flex justify-content-end">
        <Button
          icon="pi pi-file-excel"
          label="Exportar"
          @click="exportCSV()"
          class="text-sm font-light bg-green-100 border-green-100 text-green-700 mr-3"
        />

        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText v-model="filters['global'].value" placeholder="Buscar" />
        </span>
      </div>
    </template>
    <Column
      selectionMode="multiple"
      headerStyle="width: 3rem"
      v-if="!requestsAreHistoric"
    ></Column>
    <Column
      field="employee.payroll"
      header="Código de Nómina"
      sortable
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
      filter
    >
      <template #body="{ data, field }">
        <span class="font-medium text-800">{{
          data.employee.payroll.code
        }}</span>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="payrollsCode"
          optionLabel="code"
          placeholder=""
          class="p-column-filter"
          style="min-width: 14rem"
          :maxSelectedLabels="1"
          filter
        >
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <span>{{ slotProps.option.code }}</span>
            </div>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column
      field="employee.payroll"
      header="Nómina"
      sortable
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
      filter
    >
      <template #body="{ data, field }">
        <span class="font-medium text-800">{{
          data.employee.payroll.name
        }}</span>
      </template>
    </Column>
    <Column
      field="employee"
      header="Código del Empleado"
      sortable
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
    >
      <template #body="{ data, field }">
        <span class="font-medium text-800">{{ data.employee.code }}</span>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="employeesCode"
          option-label="code"
          placeholder=""
          class="p-column-filter"
          style="min-width: 14rem"
          :maxSelectedLabels="1"
          filter
        >
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <span>{{ slotProps.option.code }}</span>
            </div>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column
      field="employee.name"
      header="Nombre del Empleado"
      sortable
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
      filter
    >
      <template #body="{ data, field }">
        <span class="font-medium text-800">{{ data.employee.name }}</span>
      </template>
    </Column>
    <Column
      field="letterCertifConfig.name"
      header="Nombre de la carta"
      style="width: 20%"
      sortable
    ></Column>
    <Column field="createdAt" header="Fecha de creación" sortable>
      <template #body="{ data, field }">
        {{ formatDateWithHour(data[field].toString()) }}
      </template>
    </Column>
    <Column field="generationDate" header="Fecha de generación">
      <template #body="{ data, field }">
        <span v-if="data[field]">
          {{ formatDateWithHour(data.resolutionDate) }}</span
        >
        <span v-else>{{ 'No definida' }}</span>
      </template>
    </Column>
    <Column field="status" header="Estado" style="width: 20%" sortable>
      <template #body="{ data, field }">
        <Tag
          :value="formatRequestState(data[field])"
          :severity="getStatusLabel(data[field])"
          class=""
        ></Tag>
      </template>
      <template
        #filter="{ filterModel, filterCallback }"
        v-if="requestsAreHistoric"
      >
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="letterStatus"
          placeholder=""
          class="p-column-filter"
          style="min-width: 14rem"
          :maxSelectedLabels="1"
        >
          <template #option="slotProps">
            <Tag
              :value="formatRequestState(slotProps.option)"
              :severity="getStatusLabel(slotProps.option)"
              class=""
            ></Tag>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización" sortable>
      <template #body="{ data, field }">
        {{ formatDateWithHour(data[field].toString()) }}
      </template>
    </Column>
    <Column header="Acciones" field="steps">
      <template #body="{ data }">
        <div class="flex flex-justify-end">
          <Button
            v-if="data.status === 'PENDING' && !requestsAreHistoric"
            v-tooltip.bottom="'Procesar solicitud'"
            class="h-2rem hover:text-blue-800 mr-2"
            severity="secondary"
            icon="pi pi-check-circle"
            @click="processSingleRequest(data)"
          ></Button>
          <Button
            v-tooltip.bottom="'Ver detalles'"
            icon="pi pi-eye"
            class="h-2rem hover:text-blue-800"
            severity="primary"
            @click="goToDetail(data['id'])"
          />
        </div>
      </template>
    </Column>
  </DataTable>
  <Dialog v-model:visible="approvalDialogVisibility">
    <template #header>
      <h4>Procesar solicitud de carta</h4>
    </template>
    <template #default> </template>
    <template #footer>
      <Button
        class="font-light h-2rem"
        label="Cancelar"
        severity="secondary"
        @click="approvalDialogVisibility = false"
      ></Button>
      <Button
        class="font-light h-2rem"
        label="Rechazar"
        severity="danger"
        @click="onResolutionRequest('REJECTED')"
      ></Button>
      <Button
        class="font-light h-2rem"
        label="Aprobar"
        severity="success"
        @click="onResolutionRequest('APPROVED')"
      ></Button>
    </template>
  </Dialog>
</template>
<script setup lang="ts">
  import router from '@/router'
  import Tag from 'primevue/tag'
  import Toolbar from 'primevue/toolbar'
  import { FilterMatchMode } from 'primevue/api'
  import { storeToRefs } from 'pinia'
  import { useUserStore } from '@/store'
  import useNotifications from '@/store/notification'
  import useLoading from '@/store/load'
  import formatRequestState from '@/shared/utils/formatRequestState'
  import { onMounted, ref } from 'vue'
  import LetterRequest from '../models/LetterRequest'
  import {
    createLetterRequestResolutions,
    getLetterRequestsByApproverId,
  } from '../services/LetterRequestService'
  import { formatDateWithHour } from '@/shared/utils/fomartDateWithHours'
  import LetterRequestReportXlsx from '@/shared/utils/reports/letterRequestReportXlsx'

  const { user } = storeToRefs(useUserStore())
  const notificationStore = useNotifications()
  const loadingStore = useLoading()

  const letterRequests = ref<LetterRequest[]>([])
  const selectedRequests = ref<LetterRequest[]>()
  const requestsAreHistoric = ref(false)
  const approvalDialogVisibility = ref(false)
  const singleRequestProcessing = ref<LetterRequest>()
  const multipleRequestsProcessing = ref<LetterRequest[]>()
  const filteredRequests = ref<LetterRequest[]>()
  const letterRequestPivot = ref<LetterRequest[]>()

  //filters
  const employeesCode = ref()
  const letterStatus = ref()
  const payrollsCode = ref()
  /////

  const changeRequestHistoric = async () => {
    requestsAreHistoric.value = !requestsAreHistoric.value
    if (requestsAreHistoric.value) {
      filters.value = {
        ...filters.value,
        status: {
          value: ['GENERATED', 'REJECTED'],
          matchMode: FilterMatchMode.IN,
        },
      }

      letterRequests.value = letterRequests.value.filter(
        (lr) => lr.status !== 'PENDING'
      )
    } else {
      filters.value = {
        ...filters.value,
        status: { value: ['PENDING'], matchMode: FilterMatchMode.IN },
      }
      await refreshData()
    }
  }

  const processSingleRequest = async (request: LetterRequest) => {
    approvalDialogVisibility.value = true
    singleRequestProcessing.value = request
  }

  const processMultipleRequests = async () => {
    approvalDialogVisibility.value = true
    multipleRequestsProcessing.value = selectedRequests.value
  }

  onMounted(async () => {
    await refreshData()
  })

  const refreshData = async () => {
    if (user.value?.approverId) {
      loadingStore.setLoadingState()
      const { data, totalItems } = await getLetterRequestsByApproverId(
        user.value.approverId
      )
      letterRequests.value = data
      filteredRequests.value = data
      letterRequestPivot.value = data
      setFilters(letterRequests.value)
      loadingStore.dismissLoadingState()
    }
  }

  const onResolutionRequest = async (state: 'APPROVED' | 'REJECTED') => {
    loadingStore.setLoadingState()
    if (!user.value) return

    let resolutionBody: any = {
      userId: user.value?.id,
    }

    if (
      multipleRequestsProcessing.value &&
      multipleRequestsProcessing.value?.length > 0 &&
      singleRequestProcessing.value === undefined
    ) {
      resolutionBody = {
        ...resolutionBody,
        resolutions: multipleRequestsProcessing.value.map((lr) => ({
          letterRequestId: lr.id,
          resolutionStatus: state,
        })),
      }
    } else if (
      singleRequestProcessing.value &&
      multipleRequestsProcessing.value === undefined
    ) {
      resolutionBody = {
        ...resolutionBody,
        resolutions: [
          {
            letterRequestId: singleRequestProcessing.value.id,
            resolutionStatus: state,
          },
        ],
      }
    }

    const response = await createLetterRequestResolutions(resolutionBody)

    //here call the service to process multiple requests
    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Solicitud(es) procesada(s) exitosamente`,
      })
    }

    approvalDialogVisibility.value = false
    singleRequestProcessing.value = undefined
    multipleRequestsProcessing.value = undefined
    selectedRequests.value = []
    await refreshData()
  }

  const exportCSV = () => {
    if (!filteredRequests.value) return
    LetterRequestReportXlsx(filteredRequests.value)
  }

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: ['PENDING'], matchMode: FilterMatchMode.IN },
    employee: { value: null, matchMode: FilterMatchMode.IN },
    'employee.code': { value: null, matchMode: FilterMatchMode.IN },
    'employee.payroll': { value: null, matchMode: FilterMatchMode.IN },
  })

  const setFilters = (requests: any) => {
    employeesCode.value = requests.reduce((acc: any, curr: any) => {
      if (!acc.find((e: any) => e.id === curr.employee.id)) {
        acc.push(curr.employee)
      }
      return acc
    }, [])

    payrollsCode.value = requests.reduce((acc: any, curr: any) => {
      if (!acc.find((e: any) => e.id === curr.employee.payroll.id)) {
        acc.push(curr.employee.payroll)
      }
      return acc
    }, [])

    letterStatus.value = ['REJECTED', 'GENERATED']
  }

  const goToDetail = (id: string) => {
    router.push('/letterRequests/' + id)
  }

  const onFilter = (event: any) => {
    const { filteredValue } = event
    const filteredValueCasted = filteredValue as LetterRequest[]
    filteredRequests.value =
      filteredValueCasted.length > 0
        ? filteredValueCasted
        : letterRequests.value
  }

  //TODO: move it to shared utils
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'
      case 'PENDING':
        return 'warning'
      case 'REJECTED':
        return 'danger'
      case 'GENERATED':
        return 'info'
      default:
        return 'warning'
    }
  }
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
